import { get, post, patch } from './axios.config';
import tenantsService from './tenants.service';
import localStorageService from '../storage/localStorage.service';

/**
 * Trata erros da API e faz retry se necessário
 */
const handleApiCall = async (apiCall, retryOnAuth = true) => {
  try {
    return await apiCall();
  } catch (error) {
    // Se erro 401 e não é admin, tenta renovar token
    if (error.response?.status === 401 && retryOnAuth) {
      const userData = localStorageService.getUserData();
      const isAdmin = userData?.type === 'admin' || userData?.role === 'admin';
      
      if (!isAdmin && userData?.tenantId) {
        console.log('🔄 Token expirado, renovando...');
        
        try {
          // Gera novo token
          const tokenData = await tenantsService.generateToken(userData.tenantId);
          localStorageService.setAuthToken(tokenData.token);
          
          console.log('✅ Token renovado, tentando novamente...');
          
          // Retry a chamada
          return await apiCall();
        } catch (retryError) {
          console.error('❌ Erro ao renovar token:', retryError);
          throw new Error('Sessão expirada. Faça login novamente.');
        }
      }
    }
    
    // Se não conseguiu tratar, lança o erro
    throw handleError(error);
  }
};

/**
 * Formata erros da API
 */
const handleError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    
    if (status === 401) {
      return new Error('Não autorizado. Faça login novamente.');
    }
    
    if (status === 403) {
      return new Error('Você não tem permissão para acessar este recurso.');
    }
    
    if (status === 404) {
      return new Error('Recurso não encontrado.');
    }
    
    return new Error(data?.error || data?.message || 'Erro ao processar requisição');
  }
  
  if (error.request) {
    return new Error('Sem resposta do servidor. Verifique sua conexão.');
  }
  
  return new Error(error.message || 'Erro desconhecido');
};

/**
 * Serviço de gerenciamento de conversas (Tenant)
 */
const conversationsService = {
  /**
   * Lista conversas de um tenant
   */
  getConversations: async (tenantId, params = {}) => {
    return handleApiCall(async () => {
      const { status = 'open', limit = 50, cursor } = params;
      
      let url = `/api/tenant/${tenantId}/conversations?status=${status}&limit=${limit}`;
      
      if (cursor) {
        url += `&cursor=${cursor}`;
      }

      console.log('📡 Requisição:', url);

      const response = await get(url);
      
      console.log('✅ Resposta recebida');
      
      return response.data;
    });
  },

  /**
   * Busca mensagens de uma conversa
   */
  getMessages: async (tenantId, conversationId) => {
    return handleApiCall(async () => {
      const response = await get(`/api/tenant/${tenantId}/conversations/${conversationId}/messages`);
      return response.data;
    });
  },

  /**
   * Fecha uma conversa
   */
  closeConversation: async (tenantId, conversationId, data = {}) => {
    return handleApiCall(async () => {
      const response = await patch(
        `/api/tenant/${tenantId}/conversations/${conversationId}/close`,
        data
      );
      return response.data;
    });
  },

  /**
   * Pausa o bot para um contato (handoff)
   */
  pauseBot: async (waId, tenantId) => {
    return handleApiCall(async () => {
      const response = await post('/handoff/pause', {
        wa_id: waId,
        tenant_id: tenantId,
      });
      return response.data;
    }, false); // Handoff não precisa de retry com token
  },

  /**
   * Retoma o bot para um contato
   */
  resumeBot: async (waId, tenantId) => {
    return handleApiCall(async () => {
      const response = await post('/handoff/resume', {
        wa_id: waId,
        tenant_id: tenantId,
      });
      return response.data;
    }, false); // Handoff não precisa de retry com token
  },
};

export default conversationsService;